import React, { useState } from 'react'
import { Combobox, Menu } from '@headlessui/react'
import CDInputOutline from 'components/cd-input-outline'
import { ErrorMessage, Form, Formik, useFormikContext } from 'formik'
import AddFilledIcon from 'icons/add-filled'
import AddIcon from 'icons/add'
import ArrowDropdownIcon from 'icons/arrow-dropdown'
import RemoveIcon from 'icons/remove'
import * as yup from 'yup'
import Loader from 'components/loader'
import Api from 'utils/api'
import { useCardHolders } from 'layouts/admin/cardholder/card-holders.context'
import { useAuth } from 'context/auth'
import { isEmptyJSON, showToastMessage } from 'utils'
import ListInputField from 'components/list-input-field'
import { ROLE } from 'enums/user'
import ListInputFieldPhone from 'components/list-input-field-phone'
import CountryCodes from 'enums/countrycode'
import { validateEmail, getEmailErrorMessage } from "utils/email-validation";


const CreateCardHolder = yup.object({
  firstName: yup.string().required().label('First name'),
  lastName: yup.string().required().label('Last name'),
  role: yup.string().required().label('Role'),
  organisationName: yup.string().required().label('Company / Organisation'),
  emails: yup
    .array()
    .of(
      yup.object({
        value: yup
          .string()
          .required('Email is required')
          .test('email-format', 'Please enter a valid email address format', (value) => {
            if (!value) return false
            return /^[a-zA-Z0-9.!#$%&'*+/=?^_`{|}~-]+@[a-zA-Z0-9](?:[a-zA-Z0-9-]{0,61}[a-zA-Z0-9])?(?:\.[a-zA-Z0-9](?:[a-zA-Z0-9-]{0,61}[a-zA-Z0-9])?)+$/.test(value.trim().toLowerCase())
          })
          .test('email-validation', (value) => {
            if (!value) return false
            return validateEmail(value).isValid
          }, (value) => {
            if (!value) return 'Email is required'
            return getEmailErrorMessage(value)
          })
          .label('Email'),
      })
    )
    .min(1),
    phones: yup.array().of(
      yup.object({
        value: yup
          .string()
          .required("Phone is required")
          .test("digits", "Invalid phone number", (v) => {
            if (!v) return false
            const digits = v.replace(/[^\d]/g, "")
            return digits.length >= 7
          }),
      }),
    ),    
});

function CreateCardholder({ title, onClose, user = {} }) {
  const { currentUser } = useAuth()
  const [processing, setProcessing] = useState(false)
  const [error, setError] = useState('')

  const getPhoneDialCode = (phone) => {
    if (!phone) return ''
    const countryCode = CountryCodes.find((code) => {
      return phone.includes(code.dial_code)
    })
    return countryCode?.dial_code
  }

  const getValue = (phone) => {
    if (!phone) return ''
    const countryCode = CountryCodes.find((code) => {
      return phone.includes(code.dial_code)
    })
    if (!countryCode) return ''
    const index = phone.indexOf(countryCode.dial_code)

    if (index !== -1) {
      const extractedStr = phone.substring(countryCode.dial_code.length)
      return extractedStr
    } else return ''
  }

  const initialValues = {
    firstName: user.firstName || '',
    lastName: user.lastName || '',
    role: currentUser.account.role || user.role,
    organisationName: currentUser.account.organisationName || user.organisationName,
    emails: user.emails?.length ? user.emails : [{ value: user.email } || ''],
    phones: user.phones?.length
      ? user.phones.map((phone) => ({
          ...phone,
          value: getValue(phone.value),
          dialCode: getPhoneDialCode(phone.value),
        }))
      : user.phone
      ? [
          {
            value: getValue(user.phone),
            dialCode: getPhoneDialCode(user.phone),
          },
        ]
      : [
        {
          value: "",
          dialCode: CountryCodes[0].dial_code,
        },
      ],
  }

  const handleSubmit = async (form) => {
    setProcessing(true)
    setError('')

    form.phones = form.phones.map((phone) => {
      const raw = phone.value || ""
      const digitsOnly = raw.replace(/[^\d]/g, "")
      const localNumber =
        digitsOnly.startsWith("0") ? digitsOnly.slice(1) : digitsOnly
      return {
        type: "mobile",
        value: `${phone.dialCode}${localNumber}`,
      }
    })

    const { error, response } = isEmptyJSON(user)
      ? await Api('/user', 'post', { payload: form })
      : await Api('/user/update_user', 'put', {
          payload: { _id: user._id, ...form },
        })
    setProcessing(false)
    if (error) return setError(error.message)

    showToastMessage(
      isEmptyJSON(user)
        ? 'User successfully created'
        : 'User successfully updated'
    )
    onClose(response?.data?.user)
  }

  return (
    <>
      <h3 class='font-heading tw-text-xl md:tw-text-2xl tw-mb-4'>
        {title}
      </h3>
      <Formik
        initialValues={{ ...initialValues }}
        onSubmit={handleSubmit}
        validationSchema={CreateCardHolder}
        isInitialValid={false}
      >
        {({ isValid}) => (
          <Form className='tw-max-w-screen-sm tw-mx-auto'>
            <div class='tw-my-4 tw-gap-4 md:tw-gap-9 tw-grid md:tw-grid-cols-2'>
              <CDInputOutline
                label={'First name'}
                name={'firstName'}
                required
              />
              <CDInputOutline label={'Last name'} name={'lastName'} required />
            </div>
            <div class='tw-my-4 tw-gap-4 md:tw-gap-9 tw-grid md:tw-grid-cols-2'>
              <CDInputOutline
                label={'Role'}
                name={'role'}
                required
              />
              <CDInputOutline
                label={'Company or Organisation'}
                name={'organisationName'}
                required
                readOnly={currentUser.role != ROLE.ADMIN}
              />
            </div>
            {/* <div class='tw-my-4'>
              <CDInputOutline label={'License'} name={'license'} />
            </div> 
            <div class='tw-my-4 tw-gap-4 md:tw-gap-9'>
              <div>
                <Combobox
                  as={'div'}
                  class='tw-relative'
                  value={
                    departments.find((item) => item._id == values.department)
                      ?.name
                  }
                  onChange={(value) => setFieldValue('department', value)}
                >
                  <Combobox.Label class='tw-text-navyBlue tw-text-regular tw-leading-regular tw-flex tw-justify-between'>
                    <div class='tw-flex tw-items-center tw-gap-[5px]'>
                      Department
                    </div>
                  </Combobox.Label>
                  <Combobox.Input
                    class='tw-leading-regular tw-text-regular tw-px-[10px] tw-py-[5px] tw-w-full tw-rounded-regular tw-border tw-border-lavender tw-mt-[8px] tw-focus:border-electricGreen tw-focus:ring-transparent'
                    onChange={handlerDepartmentQuery}
                  />
                  <Combobox.Button>
                    <ArrowDropdownIcon class='tw-w-[9px] tw-h-[10px] tw-absolute tw-top-[40px] tw-right-[10px]' />
                  </Combobox.Button>
                  <Combobox.Options class='tw-max-h-36 tw-overflow-y-scroll tw-absolute tw-z-50 tw-leading-regular tw-text-regular tw-w-full tw-rounded-regular tw-border tw-border-lavender tw-mt-[8px] tw-focus:border-electricGreen tw-focus:ring-transparent tw-bg-white'>
                    {filteredDepartments.length ? (
                      filteredDepartments.map((department) => (
                        <Combobox.Option
                          key={department._id}
                          value={department._id}
                          class='tw-flex tw-items-center tw-leading-regular hover:tw-bg-lavender tw-cursor-pointer tw-px-[10px] tw-py-[5px]'
                        >
                          {department.name}
                        </Combobox.Option>
                      ))
                    ) : (
                      <>
                        <Combobox.Option
                          onClick={() => addNewDepartment(setFieldValue)}
                          class='tw-flex tw-items-center tw-leading-regular hover:tw-bg-lavender tw-cursor-pointer tw-px-[10px] tw-py-[5px]'
                        >
                          <AddIcon />
                          Add new department "{departmentQuery}"
                        </Combobox.Option>
                        <div class='tw-p-[10px] tw-text-center'>
                          No department found
                        </div>
                      </>
                    )}
                  </Combobox.Options>
                </Combobox>
              </div>
            </div> */}

            <ListInputField
              name={'emails'}
              label={'Email(s)'}
              btnLabel={'email'}
              type={'email'}
              required
              className={'tw-my-7'}
              showAddButton={false}
            />
            <ListInputFieldPhone
              name={'phones'}
              label={'Phone numbers'}
              type={'tel'}
              btnLabel={'number'}
              className={'tw-my-7'}
              showAddButton={false}
            />

            <button
              class={`tw-rounded-regular focus:tw-outline-none focus:tw-ring-transparent tw-w-full tw-text-regular tw-py-[10px] tw-px-[10px] ${
                !isValid || processing
                  ? 'tw-bg-grey tw-text-lightGrey'
                  : 'tw-bg-electricGreen tw-text-white'
              }`}
              type='submit'
              disabled={!isValid || processing}
            >
              {processing ? (
                <Loader />
              ) : isEmptyJSON(user) ? (
                'Create card'
              ) : (
                'Update card'
              )}
            </button>
            <div className='error'>{error}</div>
          </Form>
        )}
      </Formik>
    </>
  )
}

export default CreateCardholder
